import type { ThemeMode } from '../../engine/Theme';
import { SuspendTransitions } from '../SuspendTransitions/SuspendTransitions';

export type ResolvedMode = 'light' | 'dark';

export interface IThemeAttributes {
  getMode(): ResolvedMode | null;
  getReduceMotion(): boolean;
  getTheme(): string | null;
  setMode(mode: ResolvedMode): void;
  setReduceMotion(on: boolean): void;
  setTheme(name: string): void;
}

const THEME_ATTR = 'data-theme';
const MODE_ATTR = 'data-mode';
const REDUCE_MOTION_ATTR = 'data-reduce-motion';

/**
 * Reads and writes the `[data-theme]` and `[data-mode]` attributes on
 * `<html>`. The new theme runtime never writes inline `style.setProperty`
 * calls or toggles a `.dark` class — the attribute change alone re-evaluates
 * the static CSS cascade emitted by the Vite plugin. Writes are wrapped in
 * `SuspendTransitions` so the swap paints instantly.
 * `setMode` accepts a resolved mode (`'light' | 'dark'`); the `'system'`
 * preference is resolved upstream (in the pre-mount script and in
 * `ThemeProvider`) before the attribute is written.
 * `[data-reduce-motion]` is a valueless presence flag: set, the global rule in
 * `reset.css` zeroes every transition/animation; absent, motion follows the OS
 * `prefers-reduced-motion` blocks. It never re-enables motion the OS suppressed.
 */
export const ThemeAttributes: IThemeAttributes = {
  /** Reads the current resolved mode from `<html>`. Returns `null` if absent or invalid. */
  getMode(): ResolvedMode | null {
    if (typeof document === 'undefined') return null;
    const value = document.documentElement.getAttribute(MODE_ATTR);
    return value === 'light' || value === 'dark' ? value : null;
  },
  /** Whether `<html>` currently carries the user-level reduce-motion flag. */
  getReduceMotion(): boolean {
    if (typeof document === 'undefined') return false;
    return document.documentElement.hasAttribute(REDUCE_MOTION_ATTR);
  },
  /** Reads the current theme name from `<html>`. Returns `null` if absent. */
  getTheme(): string | null {
    if (typeof document === 'undefined') return null;
    return document.documentElement.getAttribute(THEME_ATTR);
  },
  /**
   * Writes `[data-mode]` on `<html>` inside a `SuspendTransitions.run`.
   * @param mode Resolved mode (callers must resolve `'system'` upstream).
   */
  setMode(mode: ResolvedMode): void {
    if (typeof document === 'undefined') return;
    SuspendTransitions.run(() => {
      document.documentElement.setAttribute(MODE_ATTR, mode);
    });
  },
  /**
   * Adds or removes the valueless `[data-reduce-motion]` flag on `<html>`,
   * inside a `SuspendTransitions.run` so the switch itself never animates.
   * @param on `true` to force reduced motion; `false` to fall back to the OS preference.
   */
  setReduceMotion(on: boolean): void {
    if (typeof document === 'undefined') return;
    SuspendTransitions.run(() => {
      if (on) document.documentElement.setAttribute(REDUCE_MOTION_ATTR, '');
      else document.documentElement.removeAttribute(REDUCE_MOTION_ATTR);
    });
  },
  /**
   * Writes `[data-theme]` on `<html>` inside a `SuspendTransitions.run`.
   * @param name Registered theme name.
   */
  setTheme(name: string): void {
    if (typeof document === 'undefined') return;
    SuspendTransitions.run(() => {
      document.documentElement.setAttribute(THEME_ATTR, name);
    });
  }
};

export type { ThemeMode };
